import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Truck, Mail, ArrowRight, Loader2, Copy, CheckCircle, MapPin, Store, CreditCard, Package } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const INTERAC_EMAIL = 'alinaelegance1@gmail.com';

export default function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user, openAuthModal } = useAuth();
  const { cart, refreshCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('interac');
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [selectedZone, setSelectedZone] = useState(null);
  const [canadaPostRates, setCanadaPostRates] = useState([]);
  const [selectedCanadaPostService, setSelectedCanadaPostService] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  
  const [shippingAddress, setShippingAddress] = useState({
    name: user?.name || '',
    street: '',
    city: '',
    province: '',
    postal_code: '',
    country: 'Canada',
    phone: '',
    delivery_distance: ''
  });

  // Province lookup from postal code
  const getProvinceFromPostalCode = (postalCode) => {
    const code = postalCode?.replace(/\s/g, '').toUpperCase() || '';
    const provinceMap = {
      'A': 'NL', 'B': 'NS', 'C': 'PE', 'E': 'NB', 'G': 'QC', 'H': 'QC', 'J': 'QC',
      'K': 'ON', 'L': 'ON', 'M': 'ON', 'N': 'ON', 'P': 'ON',
      'R': 'MB', 'S': 'SK', 'T': 'AB', 'V': 'BC',
      'X': 'NT', 'Y': 'YT'
    };
    return provinceMap[code[0]] || '';
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (user) {
      setShippingAddress(prev => ({ ...prev, name: user.name }));
    }
  }, [user]);

  // Fetch Canada Post rates when postal code changes and delivery method is canada_post
  useEffect(() => {
    if (deliveryMethod === 'canada_post' && shippingAddress.postal_code.length >= 6) {
      fetchCanadaPostRates();
    }
  }, [deliveryMethod, shippingAddress.postal_code]);

  // If Canada Post is selected and payment is COD, switch to card payment
  useEffect(() => {
    if (deliveryMethod === 'canada_post' && paymentMethod === 'cod') {
      setPaymentMethod('card');
    }
  }, [deliveryMethod]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCanadaPostRates = async () => {
    if (!shippingAddress.postal_code || shippingAddress.postal_code.length < 6) return;
    
    setLoadingRates(true);
    try {
      // Calculate weight from actual product weights + packaging (0.1 kg)
      const PACKAGING_WEIGHT = 0.1;
      const PACKAGING_MARGIN = 2; // 2cm margin for packaging
      
      const itemsWeight = cart?.items?.reduce((acc, item) => {
        const productWeight = item.weight || 0.1;
        return acc + (item.quantity * productWeight);
      }, 0) || 0;
      const totalWeight = itemsWeight + PACKAGING_WEIGHT;
      
      // Calculate box dimensions based on products
      // Strategy: Stack items, take max length/width, sum heights
      let maxLength = 0;
      let maxWidth = 0;
      let totalHeight = 0;
      
      cart?.items?.forEach(item => {
        const length = item.length || 10;
        const width = item.width || 10;
        const height = item.height || 5;
        
        maxLength = Math.max(maxLength, length);
        maxWidth = Math.max(maxWidth, width);
        totalHeight += height * item.quantity;
      });
      
      // Add packaging margin and ensure minimum dimensions
      const boxLength = Math.max(maxLength + PACKAGING_MARGIN, 10);
      const boxWidth = Math.max(maxWidth + PACKAGING_MARGIN, 10);
      const boxHeight = Math.max(totalHeight + PACKAGING_MARGIN, 5);
      
      const res = await axios.post(`${API_URL}/api/shipping/rates`, {
        postal_code: shippingAddress.postal_code,
        weight: Math.max(totalWeight, 0.1),
        length: boxLength,
        width: boxWidth,
        height: boxHeight
      });
      
      if (res.data.success && res.data.rates) {
        setCanadaPostRates(res.data.rates);
        // Auto-select cheapest option
        if (res.data.rates.length > 0) {
          const cheapest = res.data.rates.reduce((a, b) => a.total_price < b.total_price ? a : b);
          setSelectedCanadaPostService(cheapest);
          setDeliveryFee(cheapest.total_price);
        }
      }
    } catch (error) {
      console.error('Error fetching Canada Post rates:', error);
      // Use fallback rates with French names
      setCanadaPostRates([
        { service_code: 'DOM.RP', service_name: 'Colis standard', total_price: 15, transit_time: 7 },
        { service_code: 'DOM.EP', service_name: 'Colis accélérés', total_price: 20, transit_time: 3 },
        { service_code: 'DOM.XP', service_name: 'Xpresspost', total_price: 30, transit_time: 2 },
        { service_code: 'DOM.PC', service_name: 'Priorité', total_price: 40, transit_time: 1 }
      ]);
    } finally {
      setLoadingRates(false);
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(INTERAC_EMAIL);
    setCopied(true);
    toast.success('Email copié !');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDistanceChange = (distance) => {
    const dist = parseFloat(distance) || 0;
    setShippingAddress(prev => ({ ...prev, delivery_distance: distance }));
    
    if (settings?.delivery_zones && dist > 0) {
      for (const zone of settings.delivery_zones) {
        if (zone.min_distance <= dist && dist < zone.max_distance) {
          setDeliveryFee(zone.price);
          setSelectedZone(zone);
          return;
        }
      }
      // If distance exceeds all zones, use highest price
      const maxZone = settings.delivery_zones.reduce((max, z) => z.max_distance > max.max_distance ? z : max, settings.delivery_zones[0]);
      setDeliveryFee(maxZone?.price || 0);
      setSelectedZone(maxZone);
    } else {
      setDeliveryFee(0);
      setSelectedZone(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-brand-white flex flex-col items-center justify-center">
        <h1 className="font-heading text-3xl text-brand-black mb-4">{t('checkout.title')}</h1>
        <p className="text-brand-dark/60 mb-6">{t('checkout.loginToContinue')}</p>
        <Button onClick={() => openAuthModal('login')} className="bg-brand-gold text-white hover:bg-brand-gold">
          {t('nav.login')}
        </Button>
      </div>
    );
  }

  if (cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-detect province from postal code
    if (name === 'postal_code') {
      const province = getProvinceFromPostalCode(value);
      setShippingAddress(prev => ({ ...prev, [name]: value, province: province }));
    } else {
      setShippingAddress(prev => ({ ...prev, [name]: value }));
    }
    
    if (name === 'delivery_distance') {
      handleDistanceChange(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate based on delivery method
    if (deliveryMethod === 'delivery' || deliveryMethod === 'canada_post') {
      if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.postal_code || !shippingAddress.phone) {
        toast.error(t('checkout.fillAllFields'));
        return;
      }
      if (deliveryMethod === 'delivery' && !shippingAddress.delivery_distance) {
        toast.error('Veuillez indiquer la distance de livraison');
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build shipping details with Canada Post service info
      const shippingDetails = (deliveryMethod === 'delivery' || deliveryMethod === 'canada_post') ? {
        ...shippingAddress,
        canada_post_service: deliveryMethod === 'canada_post' && selectedCanadaPostService ? {
          service_code: selectedCanadaPostService.service_code,
          service_name: selectedCanadaPostService.service_name,
          price: selectedCanadaPostService.total_price,
          transit_time: selectedCanadaPostService.transit_time
        } : null
      } : null;
      
      // Create order
      const orderResponse = await axios.post(`${API_URL}/api/orders`, {
        shipping_address: shippingDetails,
        payment_method: paymentMethod,
        delivery_method: deliveryMethod,
        delivery_fee: deliveryMethod === 'pickup' ? 0 : deliveryFee,
        canada_post_service: deliveryMethod === 'canada_post' && selectedCanadaPostService ? selectedCanadaPostService.service_code : null,
        canada_post_service_name: deliveryMethod === 'canada_post' && selectedCanadaPostService ? selectedCanadaPostService.service_name : null,
        canada_post_service_code: deliveryMethod === 'canada_post' && selectedCanadaPostService ? selectedCanadaPostService.service_code : null,
        notes: paymentMethod === 'interac' ? `Paiement Interac à envoyer à: ${INTERAC_EMAIL}` : (deliveryMethod === 'canada_post' && selectedCanadaPostService ? `Expédition via ${selectedCanadaPostService.service_name}` : '')
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      const orderId = orderResponse.data.order_id;

      // If card payment, redirect to Stripe
      if (paymentMethod === 'card') {
        try {
          const stripeResponse = await axios.post(`${API_URL}/api/payments/checkout`, {
            order_id: orderId,
            origin_url: window.location.origin
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Redirect to Stripe Checkout
          window.location.href = stripeResponse.data.url;
          return;
        } catch (stripeError) {
          console.error('Stripe error:', stripeError);
          toast.error('Erreur lors de la redirection vers le paiement. Veuillez réessayer.');
          setLoading(false);
          return;
        }
      }

      toast.success('Commande passée avec succès !');
      
      if (paymentMethod === 'interac') {
        toast.info(`N'oubliez pas d'envoyer le paiement Interac à ${INTERAC_EMAIL}`, { duration: 8000 });
      }
      
      if (deliveryMethod === 'pickup') {
        toast.info(`Récupérez votre commande à: ${settings?.pickup_address || '130 Topham Terrace, Orléans, ON'}`, { duration: 8000 });
      }
      
      refreshCart();
      navigate('/orders');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la commande');
    } finally {
      setLoading(false);
    }
  };

  const subtotal = cart.total;
  const CANADA_POST_FEE = 15.00;
  const finalDeliveryFee = deliveryMethod === 'pickup' ? 0 : (deliveryMethod === 'canada_post' ? CANADA_POST_FEE : deliveryFee);
  const total = subtotal + finalDeliveryFee;

  return (
    <div className="min-h-screen pt-24 sm:pt-28 pb-20 bg-brand-white overflow-x-hidden" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <span className="font-accent text-brand-gold text-xs sm:text-sm tracking-[0.2em] sm:tracking-[0.3em] uppercase">{t('checkout.finalize')}</span>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-bold text-brand-black mt-1 sm:mt-2">{t('checkout.title')}</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-12">
            {/* Delivery & Payment */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              
              {/* Delivery Method */}
              <div
                className="bg-brand-cream border border-brand-gold/20 rounded-lg p-3 sm:p-6"
              >
                <h2 className="font-heading text-lg sm:text-2xl text-brand-black mb-4 sm:mb-6 flex items-center gap-2">
                  <Truck className="h-5 w-5 sm:h-6 sm:w-6 text-brand-gold flex-shrink-0" /> Mode de réception
                </h2>
                
                <RadioGroup value={deliveryMethod} onValueChange={setDeliveryMethod} className="space-y-3">
                  {/* Delivery Option */}
                  {settings?.delivery_enabled !== false && (
                    <label className={`block p-3 sm:p-4 border rounded-lg cursor-pointer transition-colors ${deliveryMethod === 'delivery' ? 'border-brand-gold bg-brand-gold/5' : 'border-brand-gold/20 hover:border-brand-gold/30'}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="delivery" id="delivery" className="border-brand-gold/30 text-brand-gold flex-shrink-0" />
                        <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Truck className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-brand-black font-semibold text-sm sm:text-base">{t('checkout.homeDelivery')}</p>
                          <p className="text-brand-dark/60 text-xs sm:text-sm truncate">{t('checkout.homeDeliveryDesc')}</p>
                        </div>
                        {deliveryMethod === 'delivery' && selectedZone && (
                          <span className="text-brand-gold font-bold text-sm flex-shrink-0">${deliveryFee.toFixed(2)}</span>
                        )}
                      </div>
                    </label>
                  )}
                  
                  {/* Canada Post Option */}
                  <label className={`block p-3 sm:p-4 border rounded-lg cursor-pointer transition-colors ${deliveryMethod === 'canada_post' ? 'border-brand-gold bg-brand-gold/5' : 'border-brand-gold/20 hover:border-brand-gold/30'}`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="canada_post" id="canada_post" className="border-brand-gold/30 text-brand-gold flex-shrink-0" />
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-brand-black font-semibold text-sm sm:text-base">{t('checkout.canadaPost')}</p>
                        <p className="text-brand-dark/60 text-xs sm:text-sm truncate">{t('checkout.canadaPostDesc')}</p>
                      </div>
                      {selectedCanadaPostService && deliveryMethod === 'canada_post' && (
                        <span className="text-brand-gold font-bold text-sm flex-shrink-0">${selectedCanadaPostService.total_price.toFixed(2)}</span>
                      )}
                    </div>
                    
                    {deliveryMethod === 'canada_post' && (
                      <div className="mt-4 space-y-3">
                        {loadingRates ? (
                          <div className="flex items-center justify-center p-4 bg-red-50 border border-red-200 rounded-lg">
                            <Loader2 className="h-5 w-5 animate-spin text-red-600 mr-2" />
                            <span className="text-sm text-brand-dark">Calcul des tarifs...</span>
                          </div>
                        ) : canadaPostRates.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-brand-dark mb-2">Choisissez un service:</p>
                            {canadaPostRates.map((rate) => (
                              <div
                                key={rate.service_code}
                                onClick={() => {
                                  setSelectedCanadaPostService(rate);
                                  setDeliveryFee(rate.total_price);
                                }}
                                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                  selectedCanadaPostService?.service_code === rate.service_code 
                                    ? 'border-brand-gold bg-brand-gold/10' 
                                    : 'border-red-200 bg-red-50 hover:border-brand-gold/50'
                                }`}
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-2">
                                    <Package className="h-4 w-4 text-red-600" />
                                    <span className="font-medium text-brand-black">{rate.service_name}</span>
                                  </div>
                                  <span className="font-bold text-brand-gold">${rate.total_price.toFixed(2)}</span>
                                </div>
                                <p className="text-xs text-brand-dark/60 mt-1">
                                  {rate.transit_time} {rate.transit_time === 1 ? 'jour' : 'jours'} ouvrables
                                  {rate.expected_delivery_date && ` - Livraison prévue: ${rate.expected_delivery_date}`}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-brand-dark">
                              📮 Entrez votre code postal pour voir les tarifs disponibles
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </label>
                  
                  {/* Pickup Option */}
                  {settings?.pickup_enabled !== false && (
                    <label className={`block p-3 sm:p-4 border rounded-lg cursor-pointer transition-colors ${deliveryMethod === 'pickup' ? 'border-brand-gold bg-brand-gold/5' : 'border-brand-gold/20 hover:border-brand-gold/30'}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="pickup" id="pickup" className="border-brand-gold/30 text-brand-gold flex-shrink-0" />
                        <div className="w-9 h-9 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Store className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-brand-black font-semibold text-sm sm:text-base">{t('checkout.pickup')}</p>
                          <p className="text-brand-dark/60 text-xs sm:text-sm truncate">{t('checkout.pickupDesc')}</p>
                        </div>
                        <span className="text-green-600 font-bold text-xs sm:text-sm flex-shrink-0 whitespace-nowrap">{t('checkout.free').toUpperCase()}</span>
                      </div>
                      
                      {deliveryMethod === 'pickup' && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <MapPin className="h-5 w-5 text-green-600 mt-0.5" />
                            <div>
                              <p className="text-green-800 font-medium">{t('checkout.pickupAddress')} :</p>
                              <p className="text-green-700">{settings?.pickup_address || '130 Topham Terrace, Orléans, ON'}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </label>
                  )}
                </RadioGroup>
              </div>

              {/* Shipping Address (only for delivery or canada_post) */}
              {(deliveryMethod === 'delivery' || deliveryMethod === 'canada_post') && (
                <div className="bg-brand-cream border border-brand-gold/20 rounded-lg p-3 sm:p-6">
                  <h2 className="font-heading text-lg sm:text-2xl text-brand-black mb-4 sm:mb-6 flex items-center gap-2">
                    <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-brand-gold flex-shrink-0" /> {t('checkout.shippingAddress')}
                  </h2>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="sm:col-span-2">
                      <Label className="text-brand-dark text-sm">{t('checkout.fullName')}</Label>
                      <Input
                        name="name"
                        value={shippingAddress.name}
                        onChange={handleInputChange}
                        required
                        autoComplete="name"
                        className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black text-base"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-brand-dark text-sm">{t('checkout.address')}</Label>
                      <Input
                        name="street"
                        value={shippingAddress.street}
                        onChange={handleInputChange}
                        required
                        autoComplete="street-address"
                        placeholder="123 Rue Exemple"
                        className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black placeholder:text-brand-dark/50 text-base"
                      />
                    </div>
                    <div>
                      <Label className="text-brand-dark text-sm">{t('checkout.city')}</Label>
                      <Input
                        name="city"
                        value={shippingAddress.city}
                        onChange={handleInputChange}
                        required
                        autoComplete="address-level2"
                        className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black text-base"
                      />
                    </div>
                    <div>
                      <Label className="text-brand-dark text-sm">{t('checkout.postalCode')}</Label>
                      <Input
                        name="postal_code"
                        value={shippingAddress.postal_code}
                        onChange={handleInputChange}
                        required
                        autoComplete="postal-code"
                        placeholder="K1C 2M8"
                        className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black placeholder:text-brand-dark/50 text-base"
                      />
                    </div>
                    <div>
                      <Label className="text-brand-dark text-sm">{t('checkout.phone')}</Label>
                      <Input
                        name="phone"
                        value={shippingAddress.phone}
                        onChange={handleInputChange}
                        required
                        autoComplete="tel"
                        placeholder="+1 613 123 4567"
                        className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black placeholder:text-brand-dark/50 text-base"
                      />
                    </div>
                    {deliveryMethod === 'delivery' && (
                      <div>
                        <Label className="text-brand-dark text-sm">Distance (km) *</Label>
                        <Input
                          name="delivery_distance"
                          type="number"
                          step="0.1"
                          value={shippingAddress.delivery_distance}
                          onChange={handleInputChange}
                          required
                          placeholder="Ex: 8"
                          className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black placeholder:text-brand-dark/50 text-base"
                        />
                        <p className="text-brand-dark/50 text-xs mt-1">Distance depuis notre boutique</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Delivery Zones Info - only for local delivery */}
                  {deliveryMethod === 'delivery' && settings?.delivery_zones && settings.delivery_zones.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-blue-800 font-medium mb-2">Tarifs de livraison locale :</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {settings.delivery_zones.map(zone => (
                          <div key={zone.zone_id} className={`flex justify-between ${selectedZone?.zone_id === zone.zone_id ? 'text-brand-gold font-semibold' : 'text-blue-700'}`}>
                            <span>{zone.name}</span>
                            <span>${zone.price.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Method */}
              <div className="bg-brand-cream border border-brand-gold/20 rounded-lg p-3 sm:p-6">
                <h2 className="font-heading text-lg sm:text-2xl text-brand-black mb-4 sm:mb-6 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 text-brand-gold flex-shrink-0" /> Mode de paiement
                </h2>
                
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                  {/* Interac e-Transfer Option */}
                  <label className={`block p-3 sm:p-4 border rounded-lg cursor-pointer transition-colors ${paymentMethod === 'interac' ? 'border-brand-gold bg-brand-gold/5' : 'border-brand-gold/20 hover:border-brand-gold/30'}`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="interac" id="interac" className="border-brand-gold/30 text-brand-gold flex-shrink-0" />
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-orange-400 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-white text-xs">e</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-brand-black font-semibold text-sm sm:text-base">Virement Interac</p>
                        <p className="text-brand-dark/60 text-xs sm:text-sm truncate">Envoyez le paiement par courriel</p>
                      </div>
                    </div>
                    
                    {paymentMethod === 'interac' && (
                      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-xs sm:text-sm text-brand-dark mb-2">
                          <strong>Instructions :</strong> Envoyez via Interac à :
                        </p>
                        <div className="flex items-center gap-2 bg-brand-white p-2 sm:p-3 rounded border border-orange-300">
                          <Mail className="h-4 w-4 text-brand-gold flex-shrink-0" />
                          <span className="font-mono text-brand-black font-semibold text-xs sm:text-sm flex-1 truncate">{INTERAC_EMAIL}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={copyEmail}
                            className="text-brand-gold hover:bg-brand-gold/10 p-1 h-auto"
                          >
                            {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    )}
                  </label>
                  
                  {/* Cash on Delivery Option - NOT available for Canada Post */}
                  {deliveryMethod !== 'canada_post' && (
                    <label className={`block p-3 sm:p-4 border rounded-lg cursor-pointer transition-colors ${paymentMethod === 'cod' ? 'border-brand-gold bg-brand-gold/5' : 'border-brand-gold/20 hover:border-brand-gold/30'}`}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="cod" id="cod" className="border-brand-gold/30 text-brand-gold flex-shrink-0" />
                        <div className="w-9 h-9 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Truck className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-brand-black font-semibold text-sm sm:text-base">
                            {deliveryMethod === 'pickup' ? 'Paiement au retrait' : 'Paiement à la livraison'}
                          </p>
                          <p className="text-brand-dark/60 text-xs sm:text-sm truncate">Payez en espèces ou par carte</p>
                        </div>
                      </div>
                      
                      {paymentMethod === 'cod' && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-xs sm:text-sm text-brand-dark">
                            💵 Préparez le montant exact ou une carte.
                          </p>
                        </div>
                      )}
                    </label>
                  )}
                  
                  {/* Credit Card Option */}
                  <label className={`block p-3 sm:p-4 border rounded-lg cursor-pointer transition-colors ${paymentMethod === 'card' ? 'border-brand-gold bg-brand-gold/5' : 'border-brand-gold/20 hover:border-brand-gold/30'}`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="card" id="card" className="border-brand-gold/30 text-brand-gold flex-shrink-0" />
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-brand-black font-semibold text-sm sm:text-base">Carte bancaire</p>
                        <p className="text-brand-dark/60 text-xs sm:text-sm truncate">Visa, Mastercard, Amex</p>
                      </div>
                    </div>
                    
                    {paymentMethod === 'card' && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs sm:text-sm text-brand-dark">
                          🔒 Redirection vers Stripe pour paiement sécurisé.
                        </p>
                      </div>
                    )}
                  </label>
                </RadioGroup>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-brand-cream border border-brand-gold/20 rounded-lg p-4 sm:p-6 lg:sticky lg:top-28">
                <h2 className="font-heading text-lg sm:text-2xl text-brand-black mb-4 sm:mb-6">{t('checkout.yourOrder')}</h2>
                
                <div className="space-y-3 max-h-48 sm:max-h-64 overflow-y-auto">
                  {cart.items.map(item => (
                    <div key={item.product_id} className="flex gap-2 sm:gap-3">
                      <img 
                        src={item.image || 'https://via.placeholder.com/60'} 
                        alt={item.name}
                        className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-brand-black text-xs sm:text-sm line-clamp-1">{item.name}</p>
                        <p className="text-brand-dark/60 text-xs">{t('checkout.qty')}: {item.quantity}</p>
                      </div>
                      <p className="text-brand-black font-semibold text-sm flex-shrink-0">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-brand-gold/20 mt-4 sm:mt-6 pt-4 sm:pt-6 space-y-2 sm:space-y-3">
                  <div className="flex justify-between text-brand-dark/80 text-sm">
                    <span>{t('cart.subtotal')}</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-brand-dark/80 text-sm">
                    <span>
                      {deliveryMethod === 'pickup' ? 'Retrait' : 
                       deliveryMethod === 'canada_post' ? 'Poste Canada' : 'Livraison'}
                    </span>
                    <span className={finalDeliveryFee === 0 ? 'text-green-600 font-medium' : ''}>
                      {finalDeliveryFee === 0 ? 'GRATUIT' : `$${finalDeliveryFee.toFixed(2)}`}
                    </span>
                  </div>
                </div>
                
                <div className="flex justify-between py-4 sm:py-6 text-lg sm:text-xl border-t border-brand-gold/20 mt-2 sm:mt-3">
                  <span className="font-semibold text-brand-black">{t('cart.total')}</span>
                  <span className="font-bold text-brand-gold">${total.toFixed(2)} CAD</span>
                </div>
                
                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-gold text-white hover:bg-brand-gold py-4 sm:py-6 font-bold uppercase tracking-wider btn-shine rounded disabled:opacity-50 text-sm sm:text-base"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin" /> {t('checkout.processing')}
                    </>
                  ) : (
                    <>
                      {t('checkout.confirmOrder')} <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                    </>
                  )}
                </Button>
                
                {paymentMethod === 'interac' && (
                  <p className="text-xs text-brand-dark/60 text-center mt-3">
                    Commande confirmée après réception du paiement
                  </p>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
